import { useRef, CSSProperties } from 'react';
import { gsap } from 'gsap';
import { useGSAP } from '@gsap/react';
import { usePrefersReducedMotion } from '@/hooks/usePrefersReducedMotion';

interface KelpProps {
  style?: CSSProperties;
}

const Kelp = ({ style }: KelpProps) => {
  const containerRef = useRef<SVGSVGElement>(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useGSAP(
    () => {
      if (prefersReducedMotion) {
        // Static state for reduced motion
        return;
      }

      // Create fluid, underwater swaying motion for each kelp strand
      gsap.to('#kelp-strand-1', {
        rotation: 8,
        transformOrigin: '50% 100%',
        duration: 4,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
      });

      gsap.to('#kelp-strand-2', {
        rotation: -6,
        transformOrigin: '50% 100%',
        duration: 5,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 0.5,
      });

      gsap.to('#kelp-strand-3', {
        rotation: 10,
        transformOrigin: '50% 100%',
        duration: 4.5,
        ease: 'sine.inOut',
        yoyo: true,
        repeat: -1,
        delay: 1,
      });

      // Gentle glow pulsing on bioluminescent spots
      gsap.to('.kelp-glow', {
        opacity: 0.3,
        duration: 3.5,
        stagger: 0.5,
        repeat: -1,
        yoyo: true,
        ease: 'sine.inOut',
      });
    },
    { scope: containerRef, dependencies: [prefersReducedMotion] }
  );

  return (
    <svg
      ref={containerRef}
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 200 400"
      width="200"
      height="400"
      style={style}
      aria-hidden="true"
    >
      <defs>
        <filter id="kelp-glow-filter">
          <feGaussianBlur stdDeviation="2" result="coloredBlur" />
          <feMerge>
            <feMergeNode in="coloredBlur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="kelp-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="#00FFCC" stopOpacity="0.8" />
          <stop offset="100%" stopColor="#006666" stopOpacity="0.9" />
        </linearGradient>

        {/* Fade to darkness gradient - makes kelp disappear into the depths */}
        <linearGradient id="kelp-fade" x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="0%" stopColor="white" stopOpacity="1" />
          <stop offset="70%" stopColor="white" stopOpacity="1" />
          <stop offset="100%" stopColor="white" stopOpacity="0" />
        </linearGradient>
        <mask id="kelp-fade-mask">
          <rect x="0" y="0" width="200" height="400" fill="url(#kelp-fade)" />
        </mask>
      </defs>

      {/* Apply fade mask to all kelp strands */}
      <g mask="url(#kelp-fade-mask)">
        {/* Kelp Strand 1 - Leftmost */}
        <g id="kelp-strand-1">
        <path
          d="M 40 400 Q 35 300, 40 200 Q 45 100, 40 0"
          fill="none"
          stroke="url(#kelp-gradient)"
          strokeWidth="8"
          strokeLinecap="round"
        />
        <ellipse cx="40" cy="150" rx="12" ry="20" fill="#00AA88" opacity="0.7" />
        <ellipse cx="38" cy="250" rx="10" ry="18" fill="#00AA88" opacity="0.7" />
        <circle
          className="kelp-glow"
          cx="40"
          cy="150"
          r="4"
          fill="#00FFFF"
          opacity="0.9"
          filter="url(#kelp-glow-filter)"
        />
      </g>

      {/* Kelp Strand 2 - Middle */}
      <g id="kelp-strand-2">
        <path
          d="M 100 400 Q 105 300, 100 200 Q 95 100, 100 0"
          fill="none"
          stroke="url(#kelp-gradient)"
          strokeWidth="10"
          strokeLinecap="round"
        />
        <ellipse cx="100" cy="180" rx="14" ry="22" fill="#00AA88" opacity="0.7" />
        <ellipse cx="102" cy="280" rx="12" ry="20" fill="#00AA88" opacity="0.7" />
        <ellipse cx="98" cy="90" rx="10" ry="16" fill="#00AA88" opacity="0.7" />
        <circle
          className="kelp-glow"
          cx="100"
          cy="180"
          r="5"
          fill="#00FFFF"
          opacity="0.9"
          filter="url(#kelp-glow-filter)"
        />
        <circle
          className="kelp-glow"
          cx="100"
          cy="280"
          r="4"
          fill="#00FFFF"
          opacity="0.9"
          filter="url(#kelp-glow-filter)"
        />
      </g>

      {/* Kelp Strand 3 - Rightmost */}
      <g id="kelp-strand-3">
        <path
          d="M 160 400 Q 155 300, 160 200 Q 165 100, 160 0"
          fill="none"
          stroke="url(#kelp-gradient)"
          strokeWidth="7"
          strokeLinecap="round"
        />
        <ellipse cx="160" cy="200" rx="11" ry="19" fill="#00AA88" opacity="0.7" />
        <ellipse cx="162" cy="320" rx="9" ry="16" fill="#00AA88" opacity="0.7" />
        <circle
          className="kelp-glow"
          cx="160"
          cy="200"
          r="3.5"
          fill="#00FFFF"
          opacity="0.9"
          filter="url(#kelp-glow-filter)"
        />
      </g>
      </g> {/* Close fade mask wrapper */}
    </svg>
  );
};

export default Kelp;
